import { captureReferrer, clearReferrer, referralLink } from './referrals';
import { GatewayClient, GatewayError } from '../gateway/GatewayClient';
import type {
  AccountRegistration,
  BrowserSession,
  LegacyLanguage,
  VillageSummary,
  VillageTravelPayload,
} from '../gateway/contracts';
import type { LoginNewsPayload } from '../gateway/contracts';
import { loadingView } from '../ui/LoadingView';
import type { AccountCreationOutcome } from '../ui/AccountCreationView';
import { LoginView } from '../ui/LoginView';
import type { WorldView } from '../ui/WorldView';
import { reduceAppState, type AppEvent, type AppState } from './AppState';
import { browserClientIdentity } from './BrowserIdentity';
import { GenerationFence } from './GenerationFence';
import { WorldModuleLoadError } from './WorldModuleLoadError';
import { CLIENT_UPDATE_MESSAGE } from '../ui/ClientUpdateBanner';
import {
  isReconnectableSessionError,
  isServerCapacityWait,
  isTerminalSessionError,
  MAXIMUM_RECONNECT_ATTEMPTS,
  reconnectDelayMilliseconds,
  SERVER_CAPACITY_RETRY_MILLISECONDS,
} from './ReconnectPolicy';
import { SessionStore } from './SessionStore';
import { PHONE_MEDIA_QUERY } from '../ui/MobileWorldControls';
import { VisitedVillageHistory } from './VisitedVillageHistory';
import {
  type VillageTravelIntent,
  VillageTravelCoordinator,
  VillageTravelSupersededError,
} from './VillageTravelCoordinator';
import { LegacyStartupAssetLoader } from './LegacyStartupAssetLoader';
import { legacyLoginRejection } from './LegacyLoginRejection';
import { legacyLanguageHtmlTag, loadLegacyLanguage, saveLegacyLanguage } from './LegacyLanguage';
import {
  isReplacedSession,
  REPLACED_SESSION_MESSAGE,
  ReplacedSessionTermination,
} from './LegacySessionTermination';
import type { BuildingPlacementCandidate } from '../world/BuildingPlacementPreview';
import type {
  MovementTutorialE2eState,
  ProductionTutorialE2eState,
  TerrainReconciliationState,
  TutorialBuildingPresentationState,
  AuthoritativeBuildingMaskCapture,
  AuthoritativeBuildingLandmark,
  CameraPresentationE2eState,
  LocalMovementPresentationE2eState,
  FootballPresentationE2eState,
  MountedAvatarPresentationE2eState,
  MoonwalkPresentationE2eState,
} from '../world/VillageTerrainRenderer';
import type { WeatherBuildingEffectTelemetry } from '../world/LegacyWeatherBuildingEffects';
import type { LegacyDirectionMarkerPresentation } from '../world/LegacyDirectionMarkers';
import { nextTombolaCommandSequence } from './TombolaCommandSequence';
import {
  loadLegacySystemSettings,
  saveLegacySystemSettings,
} from '../settings/LegacySystemSettings';
import { AdminPopupAcknowledgements } from './AdminPopupAcknowledgements';
import {
  GRAPHICS_WARNING_TITLE,
  graphicsAccelerationWarning,
  GraphicsAccelerationWarningSession,
  worldInitializationErrorMessage,
} from './GraphicsAcceleration';

const SESSION_POLL_INTERVAL_MILLISECONDS = 5_000;
const SESSION_REQUEST_TIMEOUT_MILLISECONDS = 12_000;

export class NordApplication {
  private clientUpdateAvailable = false;

  notifyClientUpdate(): void {
    this.clientUpdateAvailable = true;
    if (this.state.phase === 'world-load-failed' && this.state.moduleLoadFailed) this.render();
  }

  private gateway: GatewayClient;
  private language: LegacyLanguage;
  private readonly sessionStore = new SessionStore();
  private state: AppState = { phase: 'starting' };
  private loginView: LoginView | undefined;
  private worldView: WorldView | undefined;
  private terrainRequest: AbortController | undefined;
  private sessionPoll: number | undefined;
  private sessionPollRequest: AbortController | undefined;
  private reconnectAttempt = 0;
  private petFollowingSequence = 0;
  private petTreatSequence = 0;
  private treasurePickupSequence = 0;
  private buildingPurchaseSequence = 0;
  private buildingEditSequence = 0;
  private buildingProductionSequence = 0;
  private inventoryMoveSequence = 0;
  private mountTransitionSequence = 0;
  private mountPurchaseSequence = 0;
  private petPurchaseSequence = 0;
  private themePurchaseSequence = 0;
  private inventoryActivationSequence = 0;
  private weatherActivationSequence = 0;
  private resourceSlotMoveSequence = 0;
  private tombolaSequence = 0;
  private passwordChangeSequence = 0;
  private liveMessageSequence = Date.now();
  private threeDimensionalCameraEnabled = true;
  private readonly movementTutorialSteps = new Map<number, number>();
  private readonly visitedVillages = new VisitedVillageHistory();
  private villageTravel = new VillageTravelCoordinator();
  private readonly worldMounts = new GenerationFence();
  private readonly startupAssets = new LegacyStartupAssetLoader();
  private readonly adminPopupAcknowledgements = new AdminPopupAcknowledgements();
  private readonly graphicsWarningSession = new GraphicsAccelerationWarningSession();
  private phoneNoticeCleanup: (() => void) | undefined;
  private pendingWorldMessage: string | undefined;
  private returningHomeAfterVisitorKick = false;
  private logoutInProgress = false;
  private lifecycleGeneration = 0;
  private loginNews: Promise<LoginNewsPayload> | undefined;
  private loginNewsRefreshAt = Infinity;

  private referrerPlayerId = captureReferrer(window.location.search);

  constructor(private readonly root: HTMLElement) {
    this.language = loadLegacyLanguage(window.location.search);
    this.gateway = new GatewayClient(this.language);
    document.documentElement.lang = legacyLanguageHtmlTag(this.language);
    window.addEventListener('pagehide', this.handlePageHide);
  }

  private readonly handlePageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) return;
    this.lifecycleGeneration += 1;
    this.gateway.disconnectRealtime();
    this.stopSessionPolling();
    this.terrainRequest?.abort();
  };

  setPerformanceWaypoint(worldX: number, worldZ: number): boolean {
    if (import.meta.env.VITE_NORD_PERFORMANCE_HOOK !== '1') return false;
    return this.worldView?.setPerformanceWaypoint(worldX, worldZ) ?? false;
  }

  async travelToSpecialVillageForE2e(villageId: number): Promise<void> {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') {
      throw new Error('The special-village E2E hook is disabled.');
    }
    const session = this.currentSession();
    if (session === undefined) throw new Error('No active session can travel.');
    await this.travelToSpecialVillage(session, villageId, new AbortController().signal);
  }

  getE2ePetTarget(petName: string): Readonly<{ x: number; y: number }> | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2ePetTarget(petName);
  }

  getE2ePublicCameraNobos(): readonly Readonly<{
    noboType: number;
    worldX: number;
    worldZ: number;
  }>[] {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return [];
    return this.loginView?.getE2ePublicCameraNobos() ?? [];
  }

  getE2eMountTarget(mountName: string): Readonly<{ x: number; y: number }> | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eMountTarget(mountName);
  }

  getE2eAvatarTarget(playerId: number): Readonly<{ x: number; y: number }> | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eAvatarTarget(playerId);
  }

  setE2eLocalAvatarHidden(hidden: boolean): boolean {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return false;
    return this.worldView?.setE2eLocalAvatarHidden(hidden) ?? false;
  }

  getE2eTreatBuildingTarget(
    preferredX: number,
    preferredY: number,
  ): Readonly<{ x: number; y: number }> | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eTreatBuildingTarget(preferredX, preferredY);
  }

  getE2eBuildingTarget(
    buildingId: number,
    preferredX: number,
    preferredY: number,
  ): Readonly<{ x: number; y: number }> | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eBuildingTarget(buildingId, preferredX, preferredY);
  }

  captureE2eAuthoritativeBuildingMask(
    buildingIds: readonly number[],
    readyResourcesOnly = false,
  ): AuthoritativeBuildingMaskCapture | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.captureE2eAuthoritativeBuildingMask(buildingIds, readyResourcesOnly);
  }

  captureE2eAuthoritativeBuildingLandmarks(
    buildingIds: readonly number[],
  ): readonly AuthoritativeBuildingLandmark[] | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.captureE2eAuthoritativeBuildingLandmarks(buildingIds);
  }

  getE2eWeatherBuildingEffects(): WeatherBuildingEffectTelemetry | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eWeatherBuildingEffects();
  }

  getE2eDirectionMarkers(): readonly LegacyDirectionMarkerPresentation[] | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eDirectionMarkers();
  }

  selectE2eBuilding(buildingId: number, preferredX: number, preferredY: number): boolean {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return false;
    return this.worldView?.selectE2eBuilding(buildingId, preferredX, preferredY) ?? false;
  }

  activateE2eBuilding(buildingId: number, preferredX: number, preferredY: number): boolean {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return false;
    return this.worldView?.activateE2eBuilding(buildingId, preferredX, preferredY) ?? false;
  }

  getE2eBuildingEditPolicy(
    buildingType: number,
  ): Readonly<{ pickup: boolean; sell: boolean }> | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eBuildingEditPolicy(buildingType);
  }

  getE2eBuildingPlacementCandidate(
    tileX: number,
    tileZ: number,
  ): BuildingPlacementCandidate | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eBuildingPlacementCandidate(tileX, tileZ);
  }

  getE2eTutorialBuildingPresentation(): TutorialBuildingPresentationState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eTutorialBuildingPresentation();
  }

  getE2eMovementTutorialState(): MovementTutorialE2eState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eMovementTutorialState();
  }

  getE2eProductionTutorialState(): ProductionTutorialE2eState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eProductionTutorialState();
  }

  getE2eTerrainReconciliationState(): TerrainReconciliationState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eTerrainReconciliationState();
  }

  getE2eCameraPresentation(): CameraPresentationE2eState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eCameraPresentation();
  }

  getE2eMountedAvatarPresentation(): MountedAvatarPresentationE2eState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eMountedAvatarPresentation();
  }

  getE2eLocalMovementPresentation(): LocalMovementPresentationE2eState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eLocalMovementPresentation();
  }

  getE2eFootballPresentation(): FootballPresentationE2eState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eFootballPresentation();
  }

  getE2eMoonwalkPresentation(playerId?: number): MoonwalkPresentationE2eState | undefined {
    if (import.meta.env.VITE_NORD_E2E_HOOK !== '1') return undefined;
    return this.worldView?.getE2eMoonwalkPresentation(playerId);
  }

  async start(): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    void this.loadLoginNews().catch(() => undefined);
    const stored = this.sessionStore.read();
    if (stored === undefined) {
      this.dispatch({ type: 'START_WITHOUT_SESSION' });
      return;
    }
    this.petFollowingSequence = this.sessionStore.readPetFollowingSequence(stored.token);
    this.petTreatSequence = this.sessionStore.readPetTreatSequence(stored.token);
    this.treasurePickupSequence = this.sessionStore.readTreasurePickupSequence(stored.token);
    this.buildingPurchaseSequence = this.sessionStore.readBuildingPurchaseSequence(stored.token);
    this.buildingEditSequence = this.sessionStore.readBuildingEditSequence(stored.token);
    this.buildingProductionSequence = this.sessionStore.readBuildingProductionSequence(
      stored.token,
    );
    this.inventoryMoveSequence = this.sessionStore.readInventoryMoveSequence(stored.token);
    this.mountTransitionSequence = this.sessionStore.readMountTransitionSequence(stored.token);
    this.mountPurchaseSequence = this.sessionStore.readMountPurchaseSequence(stored.token);
    this.petPurchaseSequence = this.sessionStore.readPetPurchaseSequence(stored.token);
    this.themePurchaseSequence = this.sessionStore.readThemePurchaseSequence(stored.token);
    this.inventoryActivationSequence = this.sessionStore.readInventoryActivationSequence(
      stored.token,
    );
    this.weatherActivationSequence = this.sessionStore.readWeatherActivationSequence(stored.token);
    this.resourceSlotMoveSequence = this.sessionStore.readResourceSlotMoveSequence(stored.token);
    this.tombolaSequence = this.sessionStore.readTombolaSequence(stored.token);
    this.passwordChangeSequence = this.sessionStore.readPasswordChangeSequence(stored.token);
    this.villageTravel = new VillageTravelCoordinator(
      this.sessionStore.readVillageTravelSequence(stored.token),
      (sequence) => this.sessionStore.writeVillageTravelSequence(stored.token, sequence),
    );
    this.root.replaceChildren(
      loadingView('Restoring your Nord session…', 5, () => void this.logout()),
    );
    try {
      let session: BrowserSession;
      for (;;) {
        try {
          try {
            session = await this.gateway.readSession(stored.token);
          } catch (error) {
            if (!(error instanceof GatewayError) || error.code !== 'legacy_disconnected')
              throw error;
            session = (await this.gateway.resumeSession(stored.token)).session;
          }
          break;
        } catch (error) {
          if (!isServerCapacityWait(error)) throw error;
          this.root.replaceChildren(
            loadingView(
              'The Nord server is full. Waiting for a place…',
              5,
              () => void this.logout(),
            ),
          );
          await delay(SERVER_CAPACITY_RETRY_MILLISECONDS);
          if (lifecycleGeneration !== this.lifecycleGeneration) return;
        }
      }
      if (lifecycleGeneration !== this.lifecycleGeneration) return;
      session = await this.synchronizeUserSettings(session);
      this.mountTransitionSequence = Math.max(
        this.mountTransitionSequence,
        session.mountTransitionSequence,
      );
      this.mountPurchaseSequence = Math.max(
        this.mountPurchaseSequence,
        session.mountPurchaseSequence,
      );
      this.petPurchaseSequence = Math.max(this.petPurchaseSequence, session.petPurchaseSequence);
      this.themePurchaseSequence = Math.max(
        this.themePurchaseSequence,
        session.themePurchaseSequence,
      );
      this.sessionStore.writeMountTransitionSequence(session.token, this.mountTransitionSequence);
      this.sessionStore.writeMountPurchaseSequence(session.token, this.mountPurchaseSequence);
      this.sessionStore.writePetPurchaseSequence(session.token, this.petPurchaseSequence);
      this.sessionStore.writeThemePurchaseSequence(session.token, this.themePurchaseSequence);
      this.sessionStore.write(session);
      await this.loadWorld(session, session.activeVillage ?? session.villages[0]);
    } catch {
      if (lifecycleGeneration !== this.lifecycleGeneration) return;
      this.sessionStore.clear();
      this.dispatch({ type: 'START_WITHOUT_SESSION', message: 'Your previous session has ended.' });
    }
  }

  private dispatch(event: AppEvent): void {
    this.state = reduceAppState(this.state, event);
    this.render();
  }

  private render(): void {
    this.phoneNoticeCleanup?.();
    this.phoneNoticeCleanup = undefined;
    const worldMountGeneration = this.worldMounts.begin();
    this.threeDimensionalCameraEnabled =
      this.worldView?.threeDimensionalCameraEnabled ?? this.threeDimensionalCameraEnabled;
    this.worldView?.dispose();
    this.worldView = undefined;
    this.loginView?.dispose();
    this.loginView = undefined;
    switch (this.state.phase) {
      case 'starting':
        this.root.replaceChildren(loadingView('Starting Nord…', 5, () => void this.logout()));
        break;
      case 'anonymous': {
        const view = new LoginView(
          {
            login: (username, password) => void this.login(username, password),
            createAccount: (registration) => this.createAccount(registration),
            loadNews: (refresh) => this.loadLoginNews(refresh),
          },
          this.state.username ?? '',
          this.state.message ?? '',
          this.state.password ?? '',
          this.startupAssets,
          this.state.loginRejection,
        );
        this.loginView = view;
        this.root.replaceChildren(view.element);
        view.focus();
        break;
      }
      case 'authenticating': {
        this.root.replaceChildren(
          loadingView('Contacting the Nord server…', 5, () => void this.logout()),
        );
        break;
      }
      case 'loading-world':
        this.root.replaceChildren(
          loadingView('Loading your village terrain…', 35, () => void this.logout()),
        );
        break;
      case 'world': {
        const worldState = this.state;
        if (worldState.village.id !== worldState.terrain.villageId) {
          this.dispatch({
            type: 'FATAL_ERROR',
            message: 'The loaded terrain does not match the active village.',
          });
          return;
        }
        this.root.replaceChildren(
          loadingView('Preparing the village renderer…', 80, () => void this.logout()),
        );
        void this.mountWorldView(
          worldState.session,
          worldState.village,
          worldState.terrain,
          worldMountGeneration,
          worldState.assetRecoveryNonce ?? 0,
        ).catch((error: unknown) => {
          if (!this.worldMounts.isCurrent(worldMountGeneration)) return;
          this.dispatch({
            type: 'WORLD_LOAD_FAILED',
            message: worldInitializationErrorMessage(error, navigator.userAgent),
            ...(error instanceof WorldModuleLoadError ? { moduleLoadFailed: true } : {}),
          });
        });
        break;
      }
      case 'world-load-failed': {
        const error = document.createElement('main');
        error.className = 'fatal-view world-load-failed-view';
        const content = document.createElement('section');
        const heading = document.createElement('h1');
        heading.textContent = 'The village could not be displayed';
        const message = document.createElement('p');
        const reloadRequired = this.clientUpdateAvailable && this.state.moduleLoadFailed;
        message.textContent = reloadRequired ? CLIENT_UPDATE_MESSAGE : this.state.message;
        const actions = document.createElement('div');
        actions.className = 'world-load-failed-view__actions';
        const retry = document.createElement('button');
        retry.textContent = reloadRequired ? 'Reload now' : 'Retry';
        retry.addEventListener('click', () => {
          if (reloadRequired) window.location.reload();
          else this.dispatch({ type: 'RETRY_WORLD' });
        });
        const login = document.createElement('button');
        login.textContent = 'Return to login';
        login.addEventListener('click', () => void this.logout());
        actions.append(retry, login);
        content.append(heading, message, actions);
        error.append(content);
        this.root.replaceChildren(error);
        break;
      }
      case 'fatal': {
        const error = document.createElement('main');
        error.className = 'fatal-view';
        const heading = document.createElement('h1');
        heading.textContent = 'Nord could not continue';
        const message = document.createElement('p');
        message.textContent = this.state.message;
        const restart = document.createElement('button');
        restart.textContent = 'Return to login';
        restart.addEventListener('click', () => {
          this.sessionStore.clear();
          this.dispatch({ type: 'SESSION_ENDED' });
        });
        error.append(heading, message, restart);
        this.root.replaceChildren(error);
        break;
      }
    }
  }

  private async login(username: string, password: string): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    this.dispatch({ type: 'LOGIN_STARTED', username });
    try {
      let session = await this.gateway.createSession(username, password, browserClientIdentity());
      if (lifecycleGeneration !== this.lifecycleGeneration) {
        void this.gateway.deleteSession(session.token, undefined, true).catch(() => undefined);
        return;
      }
      session = await this.synchronizeUserSettings(session);
      // A successful authentication starts a fresh notice lifetime, even for the same player/token.
      this.sessionStore.clear();
      this.petFollowingSequence = 0;
      this.petTreatSequence = 0;
      this.treasurePickupSequence = 0;
      this.buildingPurchaseSequence = 0;
      this.buildingEditSequence = 0;
      this.buildingProductionSequence = 0;
      this.inventoryMoveSequence = 0;
      this.mountTransitionSequence = session.mountTransitionSequence;
      this.mountPurchaseSequence = session.mountPurchaseSequence;
      this.petPurchaseSequence = session.petPurchaseSequence;
      this.themePurchaseSequence = session.themePurchaseSequence;
      this.inventoryActivationSequence = 0;
      this.weatherActivationSequence = 0;
      this.resourceSlotMoveSequence = 0;
      this.tombolaSequence = 0;
      this.passwordChangeSequence = 0;
      this.villageTravel.cancel();
      this.villageTravel = new VillageTravelCoordinator(0, (sequence) =>
        this.sessionStore.writeVillageTravelSequence(session.token, sequence),
      );
      this.sessionStore.writePetFollowingSequence(session.token, 0);
      this.sessionStore.writePetTreatSequence(session.token, 0);
      this.sessionStore.writeTreasurePickupSequence(session.token, 0);
      this.sessionStore.writeBuildingPurchaseSequence(session.token, 0);
      this.sessionStore.writeBuildingEditSequence(session.token, 0);
      this.sessionStore.writeBuildingProductionSequence(session.token, 0);
      this.sessionStore.writeInventoryMoveSequence(session.token, 0);
      this.sessionStore.writeThemePurchaseSequence(session.token, 0);
      this.sessionStore.writeMountTransitionSequence(session.token, this.mountTransitionSequence);
      this.sessionStore.writeMountPurchaseSequence(session.token, this.mountPurchaseSequence);
      this.sessionStore.writePetPurchaseSequence(session.token, this.petPurchaseSequence);
      this.sessionStore.writeInventoryActivationSequence(session.token, 0);
      this.sessionStore.writeWeatherActivationSequence(session.token, 0);
      this.sessionStore.writeResourceSlotMoveSequence(session.token, 0);
      this.sessionStore.writeTombolaSequence(session.token, 0);
      this.sessionStore.writePasswordChangeSequence(session.token, 0);
      this.sessionStore.writeVillageTravelSequence(session.token, 0);
      this.sessionStore.write(session);
      await this.loadWorld(session, session.activeVillage ?? session.villages[0]);
    } catch (error) {
      if (lifecycleGeneration !== this.lifecycleGeneration) return;
      this.sessionStore.clear();
      const rejection = legacyLoginRejection(error);
      this.dispatch({
        type: 'SESSION_ENDED',
        message: rejection.inlineMessage,
        loginRejection: rejection,
      });
    }
  }

  private loadLoginNews(refresh = false): Promise<LoginNewsPayload> {
    if (refresh || Date.now() >= this.loginNewsRefreshAt) this.loginNews = undefined;
    if (this.loginNews === undefined) {
      const requestedAt = Date.now();
      this.loginNewsRefreshAt = Infinity;
      this.loginNews = this.gateway.readLoginNews().then((news) => {
        this.loginNewsRefreshAt =
          news.refreshAfterMs === undefined ? Infinity : requestedAt + news.refreshAfterMs;
        return news;
      });
    }
    return this.loginNews.then((news) =>
      news.refreshAfterMs === undefined
        ? news
        : {
            ...news,
            refreshAfterMs: Math.max(0, this.loginNewsRefreshAt - Date.now()),
          },
    );
  }

  private async createAccount(registration: AccountRegistration): Promise<AccountCreationOutcome> {
    try {
      await this.gateway.createAccount(
        {
          ...registration,
          ...(this.referrerPlayerId === undefined
            ? {}
            : { referrerPlayerId: this.referrerPlayerId }),
        },
        browserClientIdentity(),
      );
      this.referrerPlayerId = undefined;
      clearReferrer();
      this.dispatch({
        type: 'ACCOUNT_CREATION_FINISHED',
        username: registration.username,
        password: registration.password,
      });
      return { created: true };
    } catch (error) {
      return {
        created: false,
        ...(error instanceof GatewayError && error.code === 'username_taken'
          ? { field: 'username' as const }
          : {}),
        message:
          error instanceof GatewayError && error.code === 'username_taken'
            ? 'The username is already taken.'
            : userFacingError(error, 'Nord could not create the account.'),
      };
    }
  }

  private async loadWorld(
    session: BrowserSession,
    village: VillageSummary | undefined,
  ): Promise<void> {
    if (village === undefined) {
      this.dispatch({ type: 'FATAL_ERROR', message: 'This account has no reachable village.' });
      return;
    }
    const inferredOwnerPlayerId =
      village.ownerPlayerId ??
      (session.villages.some((candidate) => candidate.id === village.id)
        ? session.playerId
        : undefined);
    const activeVillage: VillageSummary = {
      ...village,
      ...(inferredOwnerPlayerId === undefined ? {} : { ownerPlayerId: inferredOwnerPlayerId }),
    };
    this.dispatch({ type: 'LOGIN_SUCCEEDED', session, village: activeVillage });
    this.terrainRequest?.abort();
    const request = new AbortController();
    this.terrainRequest = request;
    try {
      const terrain = await this.gateway.readTerrain(
        session.token,
        activeVillage.id,
        request.signal,
      );
      if (!request.signal.aborted) this.dispatch({ type: 'TERRAIN_LOADED', terrain });
    } catch (error) {
      if (request.signal.aborted) return;
      if (isReconnectableSessionError(error)) {
        this.beginReconnect(session, error);
        return;
      }
      this.dispatch({
        type: 'FATAL_ERROR',
        message: userFacingError(error, 'Nord could not load the village terrain.'),
      });
    }
  }

  private async mountWorldView(
    session: BrowserSession,
    village: BrowserSession['villages'][number],
    terrain: Extract<AppState, { phase: 'world' }>['terrain'],
    generation: number,
    assetRecoveryNonce = 0,
  ): Promise<void> {
    const { WorldView } = await import('../ui/WorldView').catch((error: unknown) => {
      throw new WorldModuleLoadError(error);
    });
    const loginNews = await this.loadLoginNews().catch(() => undefined);
    if (
      !this.worldMounts.isCurrent(generation) ||
      this.state.phase !== 'world' ||
      this.state.session.token !== session.token
    )
      return;
    const view = new WorldView(
      session,
      village,
      terrain,
      () => void this.logout(),
      (chunkX, chunkZ, signal) =>
        this.gateway.readEntityChunk(session.token, village.id, chunkX, chunkZ, signal),
      (signal) => this.gateway.readRaceBuildings(session.token, village.id, signal),
      async (signal) =>
        (await this.gateway.readEntityRevision(session.token, village.id, signal)).revision,
      (signal) => this.gateway.readTerrain(session.token, village.id, signal),
      (signal, afterRevision) =>
        this.gateway.readTerrainRevision(session.token, village.id, signal, afterRevision),
      (signal) => this.gateway.readDecorationOccupancy(session.token, village.id, signal),
      (signal) => this.gateway.readObjectCollision(session.token, village.id, signal),
      (signal) => this.gateway.readPresence(session.token, village.id, signal),
      (signal) => this.gateway.readBalls(session.token, village.id, signal),
      (signal) => this.gateway.readCharacters(session.token, village.id, signal),
      (signal) => this.gateway.readVillageChat(session.token, village.id, signal),
      (signal) => this.gateway.readLiveMessages(session.token, signal),
      (signal) => this.gateway.readNordTime(session.token, signal),
      (streamGeneration, afterCursor, entityRevision, signal) =>
        this.gateway.waitForNotifications(
          session.token,
          village.id,
          streamGeneration,
          afterCursor,
          entityRevision,
          signal,
        ),
      (text, signal) => this.gateway.submitVillageChat(session.token, village.id, text, signal),
      (signal) => this.gateway.readVIPWebsite(session.token, village.id, signal),
      (prompt, signal) => this.gateway.editVIPWebsite(session.token, village.id, prompt, signal),
      (text, signal) => {
        this.liveMessageSequence = Math.max(Date.now(), this.liveMessageSequence + 1);
        return this.gateway.submitLiveMessage(
          session.token,
          this.liveMessageSequence,
          text,
          signal,
        );
      },
      (sequence, signal) =>
        this.gateway.removeVillageChatMessage(session.token, village.id, sequence, signal),
      (sequence, signal) => this.gateway.removeLiveMessage(session.token, sequence, signal),
      {
        loadIndex: (signal) => this.gateway.readForumIndex(session.token, signal),
        loadThreads: (forumId, page, signal) =>
          this.gateway.readForumThreads(session.token, forumId, page, signal),
        loadMessages: (threadId, page, signal) =>
          this.gateway.readForumMessages(session.token, threadId, page, signal),
        search: (query, page, signal) =>
          this.gateway.searchForum(session.token, query, page, signal),
        createThread: (forumId, name, message, signal) =>
          this.gateway.createForumThread(session.token, forumId, name, message, signal),
        addReply: (threadId, message, signal) =>
          this.gateway.addForumReply(session.token, threadId, message, signal),
        editMessage: (threadId, messageId, message, signal) =>
          this.gateway.editForumMessage(session.token, threadId, messageId, message, signal),
        deleteMessage: (threadId, messageId, signal) =>
          this.gateway.deleteForumMessage(session.token, threadId, messageId, signal),
        setSticky: (threadId, sticky, signal) =>
          this.gateway.setForumThreadSticky(session.token, threadId, sticky, signal),
        setLocked: (threadId, locked, signal) =>
          this.gateway.setForumThreadLocked(session.token, threadId, locked, signal),
        loadPicture: (playerId, pictureId, signal) =>
          playerId === session.playerId
            ? this.gateway.readPlayerPicture(session.token, pictureId, true, signal)
            : this.gateway.readRemotePlayerProfilePicture(
                session.token,
                playerId,
                pictureId,
                true,
                signal,
              ),
      },
      (movement, signal) =>
        this.gateway.submitMovement(
          session.token,
          village.id,
          movement.sequence,
          movement.posX,
          movement.posY,
          movement.posZ,
          movement.rotation,
          movement.boostActive,
          signal,
        ),
      (trick, signal) => this.gateway.submitPetTrick(session.token, village.id, trick, signal),
      (targetPlayerId, signal) =>
        this.gateway.submitPetTraining(session.token, village.id, targetPlayerId, signal),
      (following, characterId, position, signal) => {
        const sequence = ++this.petFollowingSequence;
        this.sessionStore.writePetFollowingSequence(session.token, sequence);
        return this.gateway.submitPetFollowing(
          session.token,
          village.id,
          sequence,
          following,
          characterId,
          position,
          signal,
        );
      },
      (buildingId, signal) => {
        const sequence = ++this.petFollowingSequence;
        this.sessionStore.writePetFollowingSequence(session.token, sequence);
        return this.gateway.submitPetHouse(session.token, village.id, sequence, buildingId, signal);
      },
      (action, characterId, signal) => {
        const sequence = ++this.mountTransitionSequence;
        this.sessionStore.writeMountTransitionSequence(session.token, sequence);
        return this.gateway.submitMountTransition(
          session.token,
          village.id,
          sequence,
          action,
          characterId,
          signal,
        );
      },
      (characterId, mountName, signal) =>
        this.gateway.updateMountName(session.token, village.id, characterId, mountName, signal),
      (sourceBuildingId, trigger, signal) => {
        const sequence = ++this.mountTransitionSequence;
        this.sessionStore.writeMountTransitionSequence(session.token, sequence);
        return this.gateway.submitBuildingTeleport(
          session.token,
          village.id,
          sequence,
          sourceBuildingId,
          trigger,
          signal,
        );
      },
      (sourceBuildingId, signal) =>
        this.travelThroughBuilding(session, village.id, sourceBuildingId, signal),
      (shop, signal) => this.gateway.readMountShop(session.token, village.id, shop, signal),
      (shop, mountType, mountName, signal) => {
        const sequence = ++this.mountPurchaseSequence;
        this.sessionStore.writeMountPurchaseSequence(session.token, sequence);
        return this.gateway.purchaseMount(
          session.token,
          village.id,
          sequence,
          shop,
          mountType,
          mountName,
          signal,
        );
      },
      (buildingId, signal) =>
        this.gateway.readPetShop(session.token, village.id, buildingId, signal),
      (buildingId, petType, petName, signal) => {
        const sequence = ++this.petPurchaseSequence;
        this.sessionStore.writePetPurchaseSequence(session.token, sequence);
        return this.gateway.purchasePet(
          session.token,
          village.id,
          sequence,
          buildingId,
          petType,
          petName,
          signal,
        );
      },
      (buildingId, signal) =>
        this.gateway.readThemeShop(session.token, village.id, buildingId, signal),
      (buildingId, themeId, targetVillageId, signal) => {
        const sequence = ++this.themePurchaseSequence;
        this.sessionStore.writeThemePurchaseSequence(session.token, sequence);
        return this.gateway.purchaseTheme(
          session.token,
          village.id,
          sequence,
          buildingId,
          themeId,
          targetVillageId,
          signal,
        );
      },
      (submission) => {
        if (submission.targetVillageId === village.id) void this.loadWorld(session, village);
      },
      (buildingId, signal) => {
        const sequence = ++this.petTreatSequence;
        this.sessionStore.writePetTreatSequence(session.token, sequence);
        return this.gateway.submitPetTreat(session.token, village.id, sequence, buildingId, signal);
      },
      (buildingId, signal) => {
        const sequence = ++this.treasurePickupSequence;
        this.sessionStore.writeTreasurePickupSequence(session.token, sequence);
        return this.gateway.submitTreasurePickup(
          session.token,
          village.id,
          sequence,
          buildingId,
          signal,
        );
      },
      (buildingId, signal) =>
        this.gateway.submitSeasonalBadgePickup(session.token, village.id, buildingId, signal),
      (buildingId, signal) =>
        this.gateway.ringDoorbell(session.token, village.id, buildingId, signal),
      (buildingId, signal) =>
        this.gateway.sitOnBuilding(session.token, village.id, buildingId, signal),
      (buildingId, signal) =>
        this.gateway.launchFromCannon(session.token, village.id, buildingId, signal),
      (buildingId, intensity, angleMilli, angularVelocityMilli, signal) =>
        this.gateway.setSwingIntensity(
          session.token,
          village.id,
          buildingId,
          intensity,
          angleMilli,
          angularVelocityMilli,
          signal,
        ),
      (signal) => this.gateway.setAway(session.token, village.id, signal),
      (targetPlayerId, action, signal) =>
        this.gateway.performAvatarGesture(
          session.token,
          village.id,
          targetPlayerId,
          action,
          signal,
        ),
      (goosePlayerId, signal) =>
        this.gateway.stareAtSillyGoose(session.token, village.id, goosePlayerId, signal),
      (targetPlayerId, action, signal) =>
        this.gateway.performAvatarSpawnTeleport(
          session.token,
          village.id,
          targetPlayerId,
          action,
          signal,
        ),
      (action, signal) =>
        this.gateway.performAvatarEmote(session.token, village.id, action, signal),
      (buildingId, code, signal) =>
        this.gateway.activateKeyDoor(session.token, village.id, buildingId, code, signal),
      (buildingId, code, signal) =>
        this.gateway.activateSelfUnlockDoor(session.token, village.id, buildingId, code, signal),
      (buildingId, code, signal) =>
        this.gateway.configureBuildingCode(session.token, village.id, buildingId, code, signal),
      (buildingId, text, signal) =>
        this.gateway.updateBuildingText(session.token, village.id, buildingId, text, signal),
      (
        buildingType,
        tileX,
        tileZ,
        rotation,
        gift,
        signal,
        placementY,
        tabletop,
        domino,
        liveScreenScale,
        placementOffsetX,
        placementOffsetZ,
        placementTiltX,
        placementTiltZ,
      ) => {
        const sequence = ++this.buildingPurchaseSequence;
        this.sessionStore.writeBuildingPurchaseSequence(session.token, sequence);
        return this.gateway.purchaseBuilding(
          session.token,
          village.id,
          sequence,
          buildingType,
          tileX,
          tileZ,
          rotation,
          gift,
          signal,
          placementY,
          tabletop,
          domino,
          liveScreenScale,
          placementOffsetX,
          placementOffsetZ,
          placementTiltX,
          placementTiltZ,
        );
      },
      (productType, tileX, tileZ, signal) => {
        const sequence = ++this.buildingPurchaseSequence;
        this.sessionStore.writeBuildingPurchaseSequence(session.token, sequence);
        return this.gateway.purchaseNobo(
          session.token,
          village.id,
          sequence,
          productType,
          tileX,
          tileZ,
          signal,
        );
      },
      (operation, characterId, details, signal) => {
        const sequence = ++this.buildingEditSequence;
        this.sessionStore.writeBuildingEditSequence(session.token, sequence);
        return this.gateway.editNobo(
          session.token,
          village.id,
          sequence,
          operation,
          characterId,
          details,
          signal,
        );
      },
      (characterId, signal) => {
        const sequence = ++this.buildingEditSequence;
        this.sessionStore.writeBuildingEditSequence(session.token, sequence);
        return this.gateway.sellMount(session.token, village.id, sequence, characterId, signal);
      },
      (characterId, signal) => {
        const sequence = ++this.buildingEditSequence;
        this.sessionStore.writeBuildingEditSequence(session.token, sequence);
        return this.gateway.pickupCustomVehicle(
          session.token,
          village.id,
          sequence,
          characterId,
          signal,
        );
      },
      (signal) => this.gateway.readMarketplace(session.token, signal),
      (direction, resourceId, quantity, signal) =>
        this.gateway.transferMarketplaceInventory(
          session.token,
          direction,
          resourceId,
          quantity,
          signal,
        ),
      (side, resourceId, quantity, unitPrice, signal, auction) =>
        this.gateway.createMarketplaceOrder(
          session.token,
          side,
          resourceId,
          quantity,
          unitPrice,
          signal,
          auction,
        ),
      (orderId, quantity, signal) =>
        this.gateway.fulfillMarketplaceOrder(session.token, orderId, quantity, signal),
      (orderId, signal) => this.gateway.cancelMarketplaceOrder(session.token, orderId, signal),
      (orderId, unitPrice, signal) =>
        this.gateway.bidMarketplaceOrder(session.token, orderId, unitPrice, signal),
      (operation, buildingId, placement, signal) => {
        const sequence = ++this.buildingEditSequence;
        this.sessionStore.writeBuildingEditSequence(session.token, sequence);
        return this.gateway.editBuilding(
          session.token,
          village.id,
          sequence,
          operation,
          buildingId,
          placement,
          signal,
        );
      },
      (signal) => this.gateway.readBuildingCatalog(session.token, village.id, signal),
      (expectedSize, signal) =>
        this.gateway.expandVillage(session.token, village.id, expectedSize, signal),
      () => void this.loadWorld(session, village),
      (signal, area) => this.gateway.readVillageEditor(session.token, village.id, signal, area),
      (baseRevision, placements, signal) => {
        const sequence = ++this.buildingEditSequence;
        this.sessionStore.writeBuildingEditSequence(session.token, sequence);
        return this.gateway.editBuildingBatch(
          session.token,
          village.id,
          sequence,
          baseRevision,
          placements,
          signal,
        );
      },
      (baseRevision, buildingIds, signal) => {
        const sequence = ++this.buildingEditSequence;
        this.sessionStore.writeBuildingEditSequence(session.token, sequence);
        return this.gateway.pickupBuildingBatch(
          session.token,
          village.id,
          sequence,
          baseRevision,
          buildingIds,
          signal,
        );
      },
      (signal, playerId) =>
        this.gateway.readPlayerPictures(
          session.token,
          signal,
          playerId === session.playerId ? undefined : playerId,
        ),
      (pictureId, thumbnail, signal, playerId) =>
        playerId === undefined || playerId === session.playerId
          ? this.gateway.readPlayerPicture(session.token, pictureId, thumbnail, signal)
          : this.gateway.readRemotePlayerProfilePicture(
              session.token,
              playerId,
              pictureId,
              thumbnail,
              signal,
            ),
      (pictureId, signal, playerId) =>
        this.gateway.deletePlayerPicture(
          session.token,
          pictureId,
          signal,
          playerId === session.playerId ? undefined : playerId,
        ),
      (imageBase64, thumbnailBase64, signal, playerId) =>
        this.gateway.uploadPlayerPicture(
          session.token,
          imageBase64,
          thumbnailBase64,
          signal,
          playerId === session.playerId ? undefined : playerId,
        ),
      (imageBase64, signal) => this.gateway.uploadScreenshot(session.token, imageBase64, signal),
      (signal) => this.gateway.readPlayerProfile(session.token, signal),
      (playerId, signal) => this.gateway.readRemotePlayerProfile(session.token, playerId, signal),
      (playerId, pictureId, thumbnail, signal) =>
        this.gateway.readRemotePlayerProfilePicture(
          session.token,
          playerId,
          pictureId,
          thumbnail,
          signal,
        ),
      (playerId, blocked, signal) =>
        this.gateway.setRemotePlayerBlocked(session.token, playerId, blocked, signal),
      (playerId, signal) => this.travelToPlayer(session, playerId, signal),
      (playerId, signal) => this.gateway.readPlayerVillages(session.token, playerId, signal),
      (playerId, villageId, signal) =>
        this.travelToPlayerVillage(session, playerId, villageId, signal),
      (signal) => this.gateway.readOwnedVillages(session.token, signal),
      (slot, signal) => this.gateway.unlockOwnedVillage(session.token, slot, signal),
      (villageId, signal) => this.travelToOwnedVillage(session.token, villageId, signal),
      (updatedSession) => this.retainSessionUpdate(updatedSession),
      (level) => this.retainPlayerLevel(session.token, level),
      (playerId, signal) => this.gateway.kickVisitor(session.token, village.id, playerId, signal),
      (destinationVillageId) => void this.returnHomeAfterVisitorKick(session, destinationVillageId),
      (playerId, signal) => this.gateway.readFriendList(session.token, playerId, signal),
      (playerId, signal) => this.gateway.readPlayerBadges(session.token, playerId, signal),
      (signal) => this.gateway.readBlockedPlayers(session.token, signal),
      (signal) => this.gateway.readOnlinePlayers(session.token, signal),
      (signal) => this.gateway.readHelpDirectory(session.token, signal),
      (signal) => this.gateway.readSupportIdentity(session.token, signal),
      (email, message, signal) =>
        this.gateway.submitSupportRequest(session.token, email, message, signal),
      (signal) => this.gateway.readWardrobe(session.token, signal),
      (categoryId, garmentId, signal) =>
        this.gateway.equipWardrobe(session.token, categoryId, garmentId, signal),
      (categoryId, garmentId, selection, signal) =>
        this.gateway.purchaseWardrobe(session.token, categoryId, garmentId, selection, signal),
      (requestedVillageId, signal) =>
        this.gateway.readVillageVisitors(session.token, requestedVillageId, signal),
      (signal) =>
        this.visitedVillages.loadEntries(
          session.token,
          (requestSignal) => this.gateway.readOnlinePlayers(session.token, requestSignal),
          (playerId, requestSignal) =>
            this.gateway.readRemotePlayerProfile(session.token, playerId, requestSignal),
          signal,
        ),
      (villageId, signal) => this.travelToSpecialVillage(session, villageId, signal),
      (playerId, signal) => this.gateway.removeFriend(session.token, playerId, signal),
      (playerId, signal) => this.gateway.readFriendRelationship(session.token, playerId, signal),
      (playerId, action, signal) =>
        this.gateway.updateFriendRequest(session.token, playerId, action, signal),
      (signal) => this.gateway.readGuestbookAvailability(session.token, signal),
      (signal) => this.gateway.clearGuestbookAvailability(session.token, signal),
      (playerId, offset, signal) =>
        this.gateway.readGuestbook(session.token, playerId, offset, signal),
      (playerId, postId, entryOffset, historyOffset, signal) =>
        this.gateway.readGuestbookHistory(
          session.token,
          playerId,
          postId,
          entryOffset,
          historyOffset,
          signal,
        ),
      (playerId, text, privatePost, signal) =>
        this.gateway.addGuestbookEntry(session.token, playerId, text, privatePost, signal),
      (playerId, postId, offset, signal) =>
        this.gateway.removeGuestbookEntry(session.token, playerId, postId, offset, signal),
      (playerId, postId, privatePost, offset, signal) =>
        this.gateway.setGuestbookEntryPrivate(
          session.token,
          playerId,
          postId,
          privatePost,
          offset,
          signal,
        ),
      (pictureId, signal, playerId) =>
        this.gateway.updatePresentationPicture(
          session.token,
          pictureId,
          signal,
          playerId === session.playerId ? undefined : playerId,
        ),
      (status, signal) => this.gateway.updatePlayerStatus(session.token, status, signal),
      (languages, signal) =>
        this.gateway.updatePreferredLanguages(session.token, languages, signal),
      (update, signal) =>
        this.gateway.updatePlayerPersonalInformation(session.token, update, signal),
      async (settings, language, signal) => {
        const saved = await this.gateway.updateUserSettings(
          session.token,
          language,
          settings,
          signal,
        );
        session = { ...session, userSettings: saved };
        this.retainSessionUpdate(session);
        return saved;
      },
      async (currentPassword, newPassword, repeatedPassword, signal) => {
        const previousToken = session.token;
        const sequence = ++this.passwordChangeSequence;
        this.sessionStore.writePasswordChangeSequence(previousToken, sequence);
        this.stopSessionPolling();
        try {
          const payload = await this.gateway.changePassword(
            previousToken,
            sequence,
            currentPassword,
            newPassword,
            repeatedPassword,
            signal,
          );
          if (signal.aborted || !this.isCurrentSession(previousToken)) {
            void this.gateway.deleteSession(payload.session.token);
            return payload;
          }
          // Password rotation continues this login; only fresh authentication resets the notice.
          if (this.sessionStore.phoneNoticeWasShown(previousToken)) {
            this.sessionStore.markPhoneNoticeShown(payload.session.token);
          }
          this.sessionStore.write(payload.session);
          this.sessionStore.writePasswordChangeSequence(payload.session.token, sequence);
          const villageTravelSequence = this.villageTravel.lastSequence;
          this.villageTravel.cancel();
          this.villageTravel = new VillageTravelCoordinator(villageTravelSequence, (nextSequence) =>
            this.sessionStore.writeVillageTravelSequence(payload.session.token, nextSequence),
          );
          this.sessionStore.writeVillageTravelSequence(
            payload.session.token,
            villageTravelSequence,
          );
          this.state = reduceAppState(this.state, {
            type: 'SESSION_ROTATED',
            session: payload.session,
          });
          session = payload.session;
          this.startSessionPolling(session);
          return payload;
        } catch (error) {
          if (!signal.aborted && this.isCurrentSession(previousToken)) {
            this.startSessionPolling(session);
          }
          throw error;
        }
      },
      (signal, ids) =>
        ids === undefined
          ? this.gateway.readBuildingProduction(session.token, village.id, signal)
          : this.gateway.readBuildingProductionDelta(session.token, village.id, ids, signal),
      (action, buildingId, resourceId, signal) => {
        const sequence = ++this.buildingProductionSequence;
        this.sessionStore.writeBuildingProductionSequence(session.token, sequence);
        return this.gateway.submitBuildingProduction(
          session.token,
          village.id,
          sequence,
          action,
          buildingId,
          resourceId,
          signal,
        );
      },
      (sourceIndex, destinationIndex, signal) => {
        const sequence = ++this.inventoryMoveSequence;
        this.sessionStore.writeInventoryMoveSequence(session.token, sequence);
        return this.gateway.submitInventoryMove(
          session.token,
          village.id,
          sequence,
          sourceIndex,
          destinationIndex,
          signal,
        );
      },
      (sourceIndex, signal) => {
        const sequence = ++this.inventoryActivationSequence;
        this.sessionStore.writeInventoryActivationSequence(session.token, sequence);
        return this.gateway.submitInventoryActivation(
          session.token,
          village.id,
          sequence,
          sourceIndex,
          signal,
        );
      },
      (source, destination, signal) => {
        const sequence = ++this.resourceSlotMoveSequence;
        this.sessionStore.writeResourceSlotMoveSequence(session.token, sequence);
        return this.gateway.submitResourceSlotMove(
          session.token,
          village.id,
          sequence,
          source.area,
          source.index,
          destination.area,
          destination.index,
          signal,
        );
      },
      (signal) => this.gateway.loadWeatherInventory(session.token, village.id, signal),
      (kind, signal) => {
        const sequence = ++this.weatherActivationSequence;
        this.sessionStore.writeWeatherActivationSequence(session.token, sequence);
        return this.gateway.submitWeatherActivation(
          session.token,
          village.id,
          sequence,
          kind,
          signal,
        );
      },
      (signal) => this.gateway.readTombola(session.token, village.id, signal),
      (signal) => {
        const sequence = nextTombolaCommandSequence(this.tombolaSequence);
        this.tombolaSequence = sequence;
        this.sessionStore.writeTombolaSequence(session.token, sequence);
        return this.gateway.submitTombola(session.token, village.id, sequence, 'start', signal);
      },
      (sequence, signal) =>
        this.gateway.submitTombola(session.token, village.id, sequence, 'stop', signal),
      (sequence) => this.gateway.submitTombola(session.token, village.id, sequence, 'finish'),
      (elapsedMilliseconds, moonwalkTelemetry, signal) =>
        this.gateway.submitRaceFinish(
          session.token,
          village.id,
          elapsedMilliseconds,
          moonwalkTelemetry,
          signal,
        ),
      (replacement) => this.dispatch({ type: 'TERRAIN_REPLACED', terrain: replacement }),
      async (type, signal) => {
        session = {
          ...session,
          contextorialCompletedTypes: [
            ...new Set([...session.contextorialCompletedTypes, type]),
          ].sort((left, right) => left - right),
        };
        this.retainSessionUpdate(session);
        const completion = await this.gateway.completeContextorial(session.token, type, signal);
        session = {
          ...session,
          contextorialCompletedTypes: completion.completedTypes,
        };
        this.retainSessionUpdate(session);
        return completion;
      },
      this.movementTutorialSteps.get(session.playerId) ?? 0,
      (step) => this.movementTutorialSteps.set(session.playerId, step),
      loginNews?.worldToolbar,
      loginNews?.profile,
      loginNews?.forum,
      loginNews?.contextorial,
      loginNews?.inventory,
      loginNews?.levelInformation,
      this.language,
      (language) => this.changeLanguage(language),
      (announcement) => {
        session = { ...session, announcement };
        this.retainSessionUpdate(session);
      },
      String(assetRecoveryNonce),
      {
        referralLink: referralLink(session.playerId, window.location.origin),
        loadReferrals: (beforeId, signal) =>
          this.gateway.readReferrals(session.token, beforeId, signal),
        loadCatalog: (signal) => this.gateway.readPaymentCatalog(session.token, signal),
        createCheckout: (offerKey, recipientPlayerId, signal) =>
          this.gateway.createPaymentCheckoutSession(
            session.token,
            offerKey,
            recipientPlayerId,
            signal,
          ),
        openCustomerPortal: (signal) =>
          this.gateway.createPaymentCustomerPortalSession(session.token, signal),
        redeemPromo: (code, signal) => this.gateway.redeemPromoCode(session.token, code, signal),
        onPromoRedeemed: (redemption) => {
          if (redemption.vipDaysAwarded <= 0 || session.vip) return;
          session = { ...session, vip: true };
          this.retainSessionUpdate(session);
        },
      },
      {
        start: (triggerBuildingId, signal) =>
          this.gateway.startRaceHeat(session.token, village.id, triggerBuildingId, signal),
        current: (signal) => this.gateway.readCurrentRaceHeat(session.token, village.id, signal),
        cancel: (heatId, signal) =>
          this.gateway.cancelRaceHeat(session.token, village.id, heatId, signal),
        cancelQueue: (queueId, signal) =>
          this.gateway.cancelRaceQueueParticipant(session.token, queueId, signal),
        finish: (heatId, finishBuildingId, finishNonce, moonwalkTelemetry, signal) =>
          this.gateway.submitAuthoritativeRaceFinish(
            session.token,
            village.id,
            heatId,
            finishBuildingId,
            finishNonce,
            moonwalkTelemetry,
            signal,
          ),
      },
      {
        status: (signal) => this.gateway.readLegacyRedemption(session.token, signal),
        redeem: (username, password, signal) =>
          this.gateway.redeemLegacyAccount(session.token, username, password, signal),
      },
      session.moderator === true || session.administrator === true
        ? {
            worldHistory: {
              inspect: (request, signal) =>
                this.gateway.inspectWorldHistory(session.token, village.id, request, signal),
              rollback: (request, signal) =>
                this.gateway.rollbackWorldHistory(session.token, village.id, request, signal),
            },
            loadReferrals: (query, status, beforeId, signal) =>
              this.gateway.readModerationReferrals(session.token, query, status, beforeId, signal),
            loadDashboard: (signal) => this.gateway.readModerationDashboard(session.token, signal),
            loadServerStatus: (signal) =>
              this.gateway.readModerationServerStatus(session.token, signal),
            loadOnlinePlayers: (signal) =>
              this.gateway.readModerationOnlinePlayers(session.token, signal),
            searchPlayers: (query, page, sort, direction, signal) =>
              this.gateway.searchModerationPlayers(
                session.token,
                query,
                page,
                sort,
                direction,
                signal,
              ),
            loadPlayer: (playerId, signal) =>
              this.gateway.readModerationPlayer(session.token, playerId, signal),
            loadCommunications: (signal) =>
              this.gateway.readModerationCommunications(session.token, signal),
            downloadCommunications: (signal) =>
              this.gateway.downloadModerationCommunications(session.token, signal),
            loadBans: (activeOnly, signal) =>
              this.gateway.readModerationBans(session.token, activeOnly, signal),
            sendPlayerPopup: (playerId, message, signal) =>
              this.gateway.sendModerationPopup(session.token, playerId, message, signal),
            savePlayerNote: (playerId, note, signal) =>
              this.gateway.saveModerationPlayerNote(session.token, playerId, note, signal),
            savePlayerStatus: (playerId, statusMessage, signal) =>
              this.gateway.saveModerationPlayerStatus(
                session.token,
                playerId,
                statusMessage,
                signal,
              ),
            sendGlobalPopup: (message, signal) =>
              this.gateway.sendGlobalModerationPopup(session.token, message, signal),
            setSpeedBoostMode: (enabled, signal) =>
              this.gateway.setModerationSpeedBoost(session.token, enabled, signal),
            setGhostMode: (enabled, signal) =>
              this.gateway.setModerationGhostMode(session.token, enabled, signal),
            setSillyGoose: (playerId, enabled, durationMinutes, signal) =>
              this.gateway.setModerationSillyGoose(
                session.token,
                playerId,
                enabled,
                durationMinutes,
                signal,
              ),
            kickPlayer: (playerId, signal) =>
              this.gateway.kickModerationPlayer(session.token, playerId, signal),
            banPlayer: (playerId, reason, durationMinutes, signal) =>
              this.gateway.banModerationPlayer(
                session.token,
                playerId,
                reason,
                durationMinutes,
                signal,
              ),
            banIP: (ip, reason, durationMinutes, signal) =>
              this.gateway.banModerationIP(session.token, ip, reason, durationMinutes, signal),
            revokeBan: (banId, signal) =>
              this.gateway.revokeModerationBan(session.token, banId, signal),
          }
        : undefined,
      {
        isAcknowledged: (message) => this.adminPopupAcknowledgements.has(session.playerId, message),
        acknowledge: (message) =>
          this.adminPopupAcknowledgements.acknowledge(session.playerId, message),
      },
      {
        resources: (signal) =>
          this.gateway.gateOfferingResources(session.token, village.id, signal),
        configure: (buildingId, resources, signal) =>
          this.gateway.configureGateOffering(
            session.token,
            village.id,
            buildingId,
            resources,
            signal,
          ),
      },
      (baseRevision, treeIds, signal) => {
        const sequence = ++this.buildingEditSequence;
        this.sessionStore.writeBuildingEditSequence(session.token, sequence);
        return this.gateway.removeCreativeTrees(
          session.token,
          village.id,
          sequence,
          baseRevision,
          treeIds,
          signal,
        );
      },
      (source, beforeId, signal) =>
        this.gateway.readCoinTransactions(session.token, source, beforeId, signal),
      (week, signal) => this.gateway.dailyChallenges(session.token, week, signal),
    );
    view.setYeetPlayerAction((playerId, warning, signal) =>
      this.gateway.yeetPlayer(session.token, village.id, playerId, warning, signal),
    );
    view.setTabletopAction((action, signal) =>
      this.gateway.updateTabletop(session.token, village.id, action, signal),
    );
    view.setDominoActions((since, action, signal) =>
      this.gateway.dominoes(session.token, village.id, since, action, signal),
    );
    view.setDominoStackAction((action, signal) =>
      this.gateway.dominoStack(session.token, village.id, action, signal),
    );
    view.setDiceRollAction((buildingId, signal, pieceId) =>
      this.gateway.rollDice(session.token, village.id, buildingId, signal, pieceId),
    );
    view.setThreeDimensionalCameraEnabled(this.threeDimensionalCameraEnabled);
    this.worldView = view;
    view.element.style.position = 'absolute';
    view.element.style.inset = '0';
    view.element.style.visibility = 'hidden';
    // Ready controls such as the Nord clock set their own visibility; opacity keeps every
    // descendant concealed until the staged world replaces the loading screen.
    view.element.style.opacity = '0';
    view.element.inert = true;
    this.root.append(view.element);
    try {
      await view.ready();
    } catch (error) {
      if (!this.worldMounts.isCurrent(generation) || this.worldView !== view) return;
      view.dispose();
      this.worldView = undefined;
      this.dispatch({
        type: 'WORLD_LOAD_FAILED',
        message:
          error instanceof Error
            ? error.message
            : 'The initial village buildings could not be loaded.',
      });
      return;
    }
    if (!this.worldMounts.isCurrent(generation) || this.worldView !== view) return;
    view.element.style.removeProperty('position');
    view.element.style.removeProperty('inset');
    view.element.style.removeProperty('visibility');
    view.element.style.removeProperty('opacity');
    view.element.inert = false;
    this.root.replaceChildren(view.element);
    if (this.pendingWorldMessage !== undefined) {
      view.showMessage(this.pendingWorldMessage);
      this.pendingWorldMessage = undefined;
    }
    if (this.graphicsWarningSession.shouldShow(view.graphicsAccelerationStatus)) {
      view.showMessage(
        graphicsAccelerationWarning(navigator.userAgent),
        GRAPHICS_WARNING_TITLE,
        undefined,
        {
          roomy: true,
          onShown: () => this.graphicsWarningSession.markShown(),
        },
      );
    }
    const media = window.matchMedia(PHONE_MEDIA_QUERY);
    const noticeLifecycle = this.lifecycleGeneration;
    let queued = false;
    const showPhoneNotice = (): void => {
      if (
        queued ||
        !media.matches ||
        this.sessionStore.phoneNoticeWasShown(session.token) ||
        !this.worldMounts.isCurrent(generation) ||
        this.worldView !== view
      )
        return;
      queued = true;
      view.showMessage(
        'Nord is best experienced on a computer, which is the recommended way to play. Some touch controls and phone-friendly layouts make mobile play possible, but the phone version may have limitations and glitches. Do not expect the same smooth, reliable experience as on a PC.',
        'Playing Nord on a phone',
        undefined,
        {
          roomy: true,
          shouldShow: () => {
            queued = false;
            return (
              media.matches &&
              this.lifecycleGeneration === noticeLifecycle &&
              this.worldMounts.isCurrent(generation) &&
              this.worldView === view &&
              !this.sessionStore.phoneNoticeWasShown(session.token)
            );
          },
          onShown: () => {
            if (
              this.lifecycleGeneration === noticeLifecycle &&
              this.worldMounts.isCurrent(generation) &&
              this.worldView === view
            )
              this.sessionStore.markPhoneNoticeShown(session.token);
          },
        },
      );
    };
    this.phoneNoticeCleanup?.();
    media.addEventListener('change', showPhoneNotice);
    this.phoneNoticeCleanup = () => media.removeEventListener('change', showPhoneNotice);
    showPhoneNotice();
    this.startSessionPolling(session);
  }

  private changeLanguage(language: LegacyLanguage): void {
    try {
      saveLegacyLanguage(language);
    } catch {
      // The in-memory preference still applies for this session.
    }
    if (language === this.language) return;
    this.language = language;
    this.gateway = new GatewayClient(language);
    this.loginNews = undefined;
    document.documentElement.lang = legacyLanguageHtmlTag(language);
    if (this.state.phase === 'world') this.render();
  }

  private async synchronizeUserSettings(session: BrowserSession): Promise<BrowserSession> {
    let settings = session.userSettings;
    if (!settings.configured) {
      const localSystem = loadLegacySystemSettings();
      try {
        settings = await this.gateway.updateUserSettings(session.token, this.language, localSystem);
      } catch {
        settings = { configured: false, language: this.language, system: localSystem };
      }
    }
    try {
      saveLegacySystemSettings(settings.system);
      saveLegacyLanguage(settings.language);
    } catch {
      // Database-backed settings still apply when browser storage is unavailable.
    }
    if (settings.language !== this.language) {
      this.language = settings.language;
      this.gateway = new GatewayClient(settings.language);
      this.loginNews = undefined;
      document.documentElement.lang = legacyLanguageHtmlTag(settings.language);
    }
    return { ...session, userSettings: settings };
  }

  private async returnHomeAfterVisitorKick(
    session: BrowserSession,
    destinationVillageId?: number,
  ): Promise<void> {
    if (this.returningHomeAfterVisitorKick || !this.isCurrentSession(session.token)) return;
    const homeVillage =
      session.villages.find((village) => village.id === destinationVillageId) ??
      session.villages[0];
    if (homeVillage === undefined && destinationVillageId === undefined) {
      this.worldView?.showMessage('Nord could not find your home village.');
      return;
    }
    this.returningHomeAfterVisitorKick = true;
    this.pendingWorldMessage = 'You were kicked out of the village you were visiting!';
    this.stopSessionPolling();
    this.villageTravel.cancel();
    try {
      if (
        destinationVillageId !== undefined &&
        !session.villages.some((village) => village.id === destinationVillageId)
      ) {
        const resumed = await this.gateway.resumeSession(session.token);
        await this.loadWorld(resumed.session, resumed.village);
      } else {
        await this.loadWorld(session, homeVillage);
      }
    } finally {
      this.returningHomeAfterVisitorKick = false;
    }
  }

  private async logout(): Promise<void> {
    if (this.logoutInProgress) return;
    this.logoutInProgress = true;
    const session = this.currentSession() ?? this.sessionStore.read();
    if (session !== undefined) {
      try {
        // `NM.h()` gives the original LogoutMessage 500 ms before process teardown.
        await this.gateway.deleteSession(session.token, AbortSignal.timeout(500));
      } catch {
        // The local logout still completes; gateway idle expiry closes a lost request later.
      }
    }
    this.endLocalSession();
    this.logoutInProgress = false;
  }

  private endLocalSession(): void {
    this.lifecycleGeneration += 1;
    this.stopSessionPolling();
    this.terrainRequest?.abort();
    this.sessionStore.clear();
    this.petFollowingSequence = 0;
    this.petTreatSequence = 0;
    this.treasurePickupSequence = 0;
    this.buildingPurchaseSequence = 0;
    this.buildingEditSequence = 0;
    this.buildingProductionSequence = 0;
    this.inventoryMoveSequence = 0;
    this.mountTransitionSequence = 0;
    this.mountPurchaseSequence = 0;
    this.petPurchaseSequence = 0;
    this.themePurchaseSequence = 0;
    this.inventoryActivationSequence = 0;
    this.weatherActivationSequence = 0;
    this.resourceSlotMoveSequence = 0;
    this.tombolaSequence = 0;
    this.passwordChangeSequence = 0;
    this.visitedVillages.clear();
    this.villageTravel.cancel();
    this.villageTravel = new VillageTravelCoordinator();
    this.dispatch({ type: 'SESSION_ENDED' });
  }

  private startSessionPolling(session: BrowserSession): void {
    this.stopSessionPolling();
    this.scheduleSessionCheck(session, SESSION_POLL_INTERVAL_MILLISECONDS);
  }

  private scheduleSessionCheck(session: BrowserSession, delayMilliseconds: number): void {
    this.sessionPoll = window.setTimeout(() => {
      this.sessionPoll = undefined;
      void this.checkSession(session);
    }, delayMilliseconds);
  }

  private async checkSession(session: BrowserSession): Promise<void> {
    if (!this.isCurrentSession(session.token)) return;
    const request = new AbortController();
    this.sessionPollRequest = request;
    const timeout = window.setTimeout(() => {
      request.abort(new DOMException('Session request timed out.', 'TimeoutError'));
    }, SESSION_REQUEST_TIMEOUT_MILLISECONDS);
    try {
      const refreshed = await this.gateway.readSession(session.token, request.signal);
      if (request.signal.aborted || !this.isCurrentSession(session.token)) return;
      this.retainSessionUpdate(refreshed);
      this.reconnectAttempt = 0;
      this.worldView?.setConnectionStatus(undefined);
      this.scheduleSessionCheck(refreshed, SESSION_POLL_INTERVAL_MILLISECONDS);
    } catch (error) {
      const timedOut = isTimeoutAbort(request.signal);
      if (request.signal.aborted && !timedOut) return;
      this.handleSessionCheckFailure(
        session,
        timedOut
          ? new GatewayError('gateway_interrupted', 'The Nord server did not respond in time.', 0)
          : error,
      );
    } finally {
      window.clearTimeout(timeout);
      if (this.sessionPollRequest === request) this.sessionPollRequest = undefined;
    }
  }

  private handleSessionCheckFailure(session: BrowserSession, error: unknown): void {
    if (!this.isCurrentSession(session.token)) return;
    if (isReplacedSession(error)) {
      this.presentReplacedSessionTermination();
      return;
    }
    if (isTerminalSessionError(error)) {
      this.endSessionAfterConnectionFailure(error.message);
      return;
    }
    if (isServerCapacityWait(error)) {
      this.worldView?.setConnectionStatus('The Nord server is full. Waiting for a place…');
      this.scheduleSessionCheckOrResume(session, SERVER_CAPACITY_RETRY_MILLISECONDS);
      return;
    }
    if (isReconnectableSessionError(error)) {
      this.beginReconnect(session, error);
      return;
    }
    this.scheduleSessionCheck(session, SESSION_POLL_INTERVAL_MILLISECONDS);
  }

  private beginReconnect(session: BrowserSession, error: GatewayError): void {
    if (!this.isCurrentSession(session.token)) return;
    if (this.reconnectAttempt >= MAXIMUM_RECONNECT_ATTEMPTS) {
      this.endSessionAfterConnectionFailure(
        `Nord could not restore the game-server connection. ${error.message}`,
      );
      return;
    }
    this.reconnectAttempt += 1;
    this.worldView?.setConnectionStatus(
      `Connection lost. Reconnecting… (${String(this.reconnectAttempt)}/${String(MAXIMUM_RECONNECT_ATTEMPTS)})`,
    );
    this.scheduleSessionCheckOrResume(session, reconnectDelayMilliseconds(this.reconnectAttempt));
  }

  private scheduleSessionCheckOrResume(session: BrowserSession, delayMilliseconds: number): void {
    if (this.sessionPoll !== undefined) window.clearTimeout(this.sessionPoll);
    this.sessionPoll = window.setTimeout(() => {
      this.sessionPoll = undefined;
      void this.resumeSession(session);
    }, delayMilliseconds);
  }

  private async resumeSession(session: BrowserSession): Promise<void> {
    if (!this.isCurrentSession(session.token)) return;
    const request = new AbortController();
    this.sessionPollRequest = request;
    const timeout = window.setTimeout(() => {
      request.abort(new DOMException('Session resume timed out.', 'TimeoutError'));
    }, SESSION_REQUEST_TIMEOUT_MILLISECONDS);
    try {
      const resumed = await this.gateway.resumeSession(session.token, request.signal);
      if (request.signal.aborted || !this.isCurrentSession(session.token)) return;
      this.sessionStore.write(resumed.session);
      this.worldView?.setConnectionStatus('Connection restored. Refreshing the village…');
      this.stopSessionPolling();
      await this.loadWorld(resumed.session, resumed.village);
    } catch (error) {
      const timedOut = isTimeoutAbort(request.signal);
      if (request.signal.aborted && !timedOut) return;
      const failure = timedOut
        ? new GatewayError('gateway_interrupted', 'The Nord server did not respond in time.', 0)
        : error;
      if (isReplacedSession(failure)) {
        this.presentReplacedSessionTermination();
      } else if (isTerminalSessionError(failure)) {
        this.endSessionAfterConnectionFailure(failure.message);
      } else if (isServerCapacityWait(failure)) {
        this.worldView?.setConnectionStatus('The Nord server is full. Waiting for a place…');
        this.scheduleSessionCheckOrResume(session, SERVER_CAPACITY_RETRY_MILLISECONDS);
      } else if (isReconnectableSessionError(failure)) {
        this.beginReconnect(session, failure);
      } else {
        this.endSessionAfterConnectionFailure(
          userFacingError(failure, 'Nord could not restore the game-server connection.'),
        );
      }
    } finally {
      window.clearTimeout(timeout);
      if (this.sessionPollRequest === request) this.sessionPollRequest = undefined;
    }
  }

  private endSessionAfterConnectionFailure(message: string): void {
    this.stopSessionPolling();
    this.terrainRequest?.abort();
    this.villageTravel.cancel();
    this.sessionStore.clear();
    this.visitedVillages.clear();
    this.dispatch({ type: 'SESSION_ENDED', message });
  }

  private presentReplacedSessionTermination(): void {
    this.stopSessionPolling();
    this.terrainRequest?.abort();
    this.villageTravel.cancel();
    const worldView = this.worldView;
    if (worldView === undefined) {
      this.endSessionAfterConnectionFailure(REPLACED_SESSION_MESSAGE);
      return;
    }
    const termination = new ReplacedSessionTermination(() => {
      this.sessionStore.clear();
      this.visitedVillages.clear();
      this.dispatch({ type: 'SESSION_ENDED' });
    });
    termination.start();
    worldView.showMessage(REPLACED_SESSION_MESSAGE, undefined, () => termination.dismiss());
  }

  private async travelToPlayer(
    session: BrowserSession,
    playerId: number,
    signal: AbortSignal,
  ): Promise<void> {
    await this.runVillageTravel(
      session.token,
      signal,
      (sequence, requestSignal) =>
        this.gateway.travelToPlayer(session.token, playerId, sequence, requestSignal),
      playerId === session.playerId
        ? undefined
        : (travel, requestSignal) =>
            this.recordVisitedPlayer(session, playerId, travel, requestSignal),
    );
  }

  private async travelThroughBuilding(
    session: BrowserSession,
    sourceVillageId: number,
    sourceBuildingId: number,
    signal: AbortSignal,
  ): Promise<void> {
    await this.runVillageTravel(session.token, signal, (travelSequence, requestSignal) => {
      const sequence = ++this.mountTransitionSequence;
      this.sessionStore.writeMountTransitionSequence(session.token, sequence);
      return this.gateway.travelThroughBuilding(
        session.token,
        sourceVillageId,
        travelSequence,
        sequence,
        sourceBuildingId,
        requestSignal,
      );
    });
  }

  private async travelToOwnedVillage(
    token: string,
    villageId: number,
    signal: AbortSignal,
  ): Promise<void> {
    await this.runVillageTravel(token, signal, (sequence, requestSignal) =>
      this.gateway.travelToOwnedVillage(token, villageId, sequence, requestSignal),
    );
  }

  private async travelToSpecialVillage(
    session: BrowserSession,
    villageId: number,
    signal: AbortSignal,
  ): Promise<void> {
    await this.runVillageTravel(session.token, signal, (sequence, requestSignal) =>
      this.gateway.travelToSpecialVillage(session.token, villageId, sequence, requestSignal),
    );
  }

  private async travelToPlayerVillage(
    session: BrowserSession,
    playerId: number,
    villageId: number,
    signal: AbortSignal,
  ): Promise<void> {
    await this.runVillageTravel(
      session.token,
      signal,
      (sequence, requestSignal) =>
        this.gateway.travelToPlayerVillage(
          session.token,
          playerId,
          villageId,
          sequence,
          requestSignal,
        ),
      (travel, requestSignal) => this.recordVisitedPlayer(session, playerId, travel, requestSignal),
    );
  }

  private async runVillageTravel(
    token: string,
    sourceSignal: AbortSignal,
    submit: (sequence: number, signal: AbortSignal) => Promise<VillageTravelPayload>,
    afterCommit?: (travel: VillageTravelPayload, signal: AbortSignal) => Promise<void>,
  ): Promise<void> {
    const intent = this.villageTravel.begin(sourceSignal);
    try {
      const travel = await submit(intent.sequence, intent.signal);
      this.requireCurrentVillageTravel(intent);
      const session = this.currentSession();
      if (session?.token !== token) return;
      await afterCommit?.(travel, intent.signal);
      this.requireCurrentVillageTravel(intent);
      this.stopSessionPolling();
      this.terrainRequest?.abort();
      await this.loadWorld(session, travel.village);
    } catch (error) {
      if (
        !intent.isCurrent() ||
        (error instanceof GatewayError && error.code === 'village_travel_superseded')
      ) {
        throw new VillageTravelSupersededError();
      }
      throw error;
    } finally {
      intent.finish();
    }
  }

  private requireCurrentVillageTravel(intent: VillageTravelIntent): void {
    if (!intent.isCurrent()) throw new VillageTravelSupersededError();
  }

  private async recordVisitedPlayer(
    session: BrowserSession,
    playerId: number,
    travel: VillageTravelPayload,
    signal: AbortSignal,
  ): Promise<void> {
    try {
      const profile = await this.gateway.readRemotePlayerProfile(session.token, playerId, signal);
      if (!signal.aborted) {
        this.visitedVillages.recordTravel(
          session.token,
          session.playerId,
          playerId,
          travel,
          profile,
        );
      }
    } catch {
      // Travel has already committed; a transient profile failure must not undo the switch.
    }
  }

  private retainSessionUpdate(session: BrowserSession): void {
    const current = this.currentSession();
    if (current?.token !== session.token) return;
    this.sessionStore.write(session);
    this.state = reduceAppState(this.state, { type: 'SESSION_ROTATED', session });
    this.worldView?.setPlayerLevel(session.level);
  }

  private retainPlayerLevel(token: string, level: number): void {
    if (!Number.isInteger(level) || level < 0) return;
    const session = this.currentSession();
    if (session?.token !== token || session.level === level) return;
    this.retainSessionUpdate({ ...session, level });
  }

  private stopSessionPolling(): void {
    if (this.sessionPoll !== undefined) window.clearTimeout(this.sessionPoll);
    this.sessionPoll = undefined;
    this.sessionPollRequest?.abort();
    this.sessionPollRequest = undefined;
    this.reconnectAttempt = 0;
  }

  private isCurrentSession(token: string): boolean {
    return this.currentSession()?.token === token;
  }

  private currentSession(): BrowserSession | undefined {
    return this.state.phase === 'world' ||
      this.state.phase === 'loading-world' ||
      this.state.phase === 'world-load-failed'
      ? this.state.session
      : undefined;
  }
}

function userFacingError(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0 ? error.message : fallback;
}

function isTimeoutAbort(signal: AbortSignal): boolean {
  return signal.reason instanceof DOMException && signal.reason.name === 'TimeoutError';
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
